import Button from 'components/atoms/Button';
import BottomSheet from 'components/molecules/BottomSheet';
import { BUTTON_NAMES } from 'constant/button-names';
import { useConfig } from 'context/app-config';
import i18n from 'i18n/config';
import { EstimatorSheetContentModule } from 'modules/newOrder/components/EstimatorSheetContent';
import PrimaryButtonIcon from 'packages/shared/home/components/HomeFooter/PrimaryButtonIcon';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

const StyledFooterRow = styled.div`
  position: absolute;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 16px;
  width: -webkit-fill-available;
  padding-bottom: 1.6rem;
`;

const StyledPlaceNewOrderListButton = styled(Button)`
  background-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.backgroundColor};
  border-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.borderColor};
  color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.textColor};
  font-size: 1rem;
  font-style: normal;
  line-height: 24px;
  font-weight: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.fontWeight ?? '700'};
  &:hover,
  &:focus,
  &:active,
  &.btn-primary:not(:disabled):not(.disabled):active {
    background-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.backgroundColor};
    border-color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.borderColor};
    color: ${({ theme }) => theme.components.pages.home?.placeOrderFooter?.textColor};
  }

  @media (max-width: 444px) {
    font-size: 0.9rem;
  }
  @media (max-width: 359px) {
    font-size: 0.79rem;
  }
  @media (max-width: 330px) {
    font-size: 0.83rem;
  }
  @media (max-width: 290px) {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    height: 40px;
    font-size: 0.69rem;
  }
  @media (max-width: ${window.innerWidth < 290 ? 289 : 290}px) {
    height: 40px;
    padding-left: 0.15rem;
    padding-right: 0.15rem;
    font-size: 0.59rem;
  }
`;

interface Props {
  show?: boolean;
  onClose?: () => void;
  onContinue?: () => void;
  isShowAddMoreItems?: boolean;
}

const StyledBottomSheet = styled(BottomSheet).attrs(({ theme }) => ({
  titleStyle: {
    fontSize: `${theme.components.estimatedBillBottomSheet?.title.fontSize ?? 24}px`,
  },
}))`
  max-height: ${({ theme }) => theme.components.estimatedBillBottomSheet?.maxHeight ?? '75%'};
  overflow: auto;
`;

const StyledSpacer = styled.div`
  height: 90px;
`;

const EstimatedBillBottomSheet: React.FC<Props> = ({ show, onClose, onContinue, isShowAddMoreItems = false }) => {
  const { t } = useTranslation();
  const config = useConfig();
  const isRTL = i18n.dir() === 'rtl';
  const theme = useTheme();

  const handleNewOrder = useCallback(() => {
    onContinue?.();
  }, [onContinue]);

  const Footer = useMemo(() => {
    if (!config?.showContinueButtonOnEstimator) return null;

    return (
      <StyledFooterRow>
        <StyledPlaceNewOrderListButton
          block
          size="lg"
          onClick={handleNewOrder}
          className="d-flex justify-content-center align-items-center pr-2 py-2"
          buttonName={BUTTON_NAMES.CONTINUE_ORDER}
        >
          {t('homePage.continueOrder')}
          {!theme.components.estimatedBillBottomSheet?.continueButton?.hideIcon && <PrimaryButtonIcon />}
        </StyledPlaceNewOrderListButton>
      </StyledFooterRow>
    );
  }, [config, handleNewOrder, isRTL, t, theme]);

  return (
    <StyledBottomSheet
      title={t('common.estimatedBill', 'Estimated Bill')}
      show={show}
      onClose={onClose}
      hideCloseButton={false}
      maxHeight={`${theme.components.estimatedBillBottomSheet?.maxHeight ?? 88}%`}
      hasNoSpacing
      footerComponent={Footer}
    >
      <EstimatorSheetContentModule isShowAddMoreItems={isShowAddMoreItems} isItemsExpandable={false} />
      <StyledSpacer />
    </StyledBottomSheet>
  );
};

export default EstimatedBillBottomSheet;
